<template>
  <div class="course-landing" :class="[course.slug, { banner: bannerActive }]">
    <div v-if="bannerActive" class="banner-holder">
      <div class="close-banner" @click="banner = false">
        <CloseIcon />
      </div>
      <div class="banner container" @click="subscribe">
        <div class="header-wrap">
          <p class="banner-header h5">
            Limited-time offer
          </p>
          <p class="banner-info">
            Choose the right learning option for your needs.
          </p>
        </div>
        <div class="counter">
          <div class="square days">
            <span>{{ countdown.days }}</span><span>Days</span>
          </div>
          <div class="square hours">
            <span>{{ countdown.hours }}</span><span>Hours</span>
          </div>
          <div class="square min">
            <span>{{ countdown.minutes }}</span><span>Minutes</span>
          </div>
          <div class="square sec">
            <span>{{ countdown.seconds }}</span><span>Seconds</span>
          </div>
        </div>
        <button class="btn-primary-purple-dark">
          Save {{ course.discountPercent }}%
        </button>
      </div>
    </div>
    <HeroSection :course="course" @subscribe="subscribe" />
    <div class="site-section">
      <div class="container">
        <div class="row" :class="{ 'bundle-row': course.bundle }">
          <div :class="course.bundle ? '' : 'col col-10 offset-1'">
            <div class="start">
              <h2>
                {{ course.startTitle }}
              </h2>
              <p class="body-1">
                {{ course.startDescription }}
              </p>
              <div v-if="course.bundle && course.courses.length" class="bundle">
                <template v-for="(c, i) in course.courses">
                  <CourseCard
                    :key="`courseCard${i}`" :course="{ ...c, type: 'similar', instructors: c.instructorsArr.map(i => i.name).join(', ') }" dark
                    class="col col-3 md-4 sm-6"
                  />
                  <div v-if="i < course.courses.length - 1" :key="`plusSign${i}`" class="plus-sign">
                    <PlusSign />
                  </div>
                </template>
              </div>
              <div class="stats col col-10 md-12">
                <div v-if="course.bundle" class="stat">
                  <div>{{ course.courses.length }}</div>
                  <span class="h5">Courses</span>
                </div>
                <div class="stat">
                  <div>{{ course.lessons }}</div>
                  <span class="h5">Lessons</span>
                </div>
                <div class="stat">
                  <div>{{ course.totalHours }}</div>
                  <span class="h5">Hours of video</span>
                </div>
                <div v-if="!course.bundle" class="stat">
                  <div>{{ course.exercises }}</div>
                  <span class="h5">Practical tasks</span>
                </div>
                <div v-else class="stat">
                  <div>{{ course.courses.length }}</div>
                  <span class="h5">Certificates</span>
                </div>
              </div>
            </div>
            <div v-if="!course.bundle" class="video">
              <VideoPlayer
                v-if="videoJSLoaded" slot="player" :video-id="course.videoId"
                class="video-player"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!course.bundle" class="site-section gray">
      <Curriculum
        :c="course"
      />
    </div>
    <div v-else class="site-section">
      <ProgramCardsSale :c="course" @subscribe="subscribe" />
    </div>
    <div class="site-section dark">
      <Cards :title="course.bundle ? 'This Course Gives You Lifetime Access to' : 'Get Lifetime Access to'" />
      <div class="container">
        <div class="row">
          <div class="col col-6 offset-3 learn">
            <h2>What You’ll Learn</h2>
            <p v-if="learn && learn.learn" class="body-1">
              {{ learn.learn.replace(/\xA0/g,' ') }}
            </p>
            <ul v-if="learn.list && learn.list.length">
              <template v-for="(l, idx) in learn.list">
                <li v-if="l" :key="idx" class="body-1 col-6">
                  <IconCheck /> {{ l }}
                </li>
              </template>
            </ul>
            <hr>
            <span>Our graduates work at</span>
            <div class="logos">
              <span class="logo">
                <LogoTesla />
              </span>
              <span class="logo">
                <LogoWalmart />
              </span>
              <span class="logo">
                <LogoCitibank />
              </span>
              <span class="logo">
                <LogoBooking />
              </span>
              <span class="logo">
                <LogoPaypal />
              </span>
            </div>
          </div>
          <div v-for="(instructor, idx) in course.instructorsArr" :key="idx" class="meet-author">
            <div class="col-5 col">
              <img loading="lazy" :src="`${$config.imagesUrl}/team/${instructor.image}`" :alt="instructor.name">
            </div>
            <div class="info col col-6 offset-1">
              <h2>Meet {{ course.instructorsArr[idx].name }}</h2>
              <template v-if="course.instructorsInfo && course.instructorsInfo[instructor.slug]">
                <p class="body-1" v-html="course.instructorsInfo[instructor.slug].instructorDescription[course.slug]" />
                <div class="stats">
                  <div v-for="(s, i) in course.instructorsInfo[instructor.slug].stats" :key="i" class="stat">
                    <div>{{ s.stat }}</div>
                    <span v-html="s.description" />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-6 col offset-3 buy-course">
            <h2>
              Buy Once. <br> Enjoy Lifetime Access.
            </h2>
            <div class="row">
              <div class="col">
                <div class="col-8 sm-6 col btn-info">
                  <button class="btn-primary btn-lg" @click="subscribe">
                    Start Learning Now
                  </button>
                  <p>
                    One-time payment. Lifetime access to all course materials and updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="site-section gray">
      <Reviews
        :reviews-data="course.reviews"
        :title="`${course.countStudents} Students Have Joined the Course!`"
        title-class="col col-6"
      />
      <FAQ />
    </div>
    <div v-if="!course.bundle" class="limited-offer-holder">
      <LimitedOffer
        :options="limitedOfferOptions"
        :features="limitedOfferFeatures"
        @subscribe="subscribe"
      />
    </div>
    <div v-else class="bottom-offer">
      <div class="container">
        <div class="row head">
          <div class="col col-6 offset-3">
            <h2>{{ bottomOfferInfo.title }}</h2>
            <p class="body-1">
              {{ bottomOfferInfo.description }}
            </p>
          </div>
        </div>
        <div class="content row">
          <div
            v-for="(props, i) in bottomOfferInfo.plans" :key="i" class="col col-4"
            :class="{ 'offset-2': i === 0 }"
          >
            <PriceCard :props="{ ...props, coupon: bundleDiscounts[props.plan], promoIsActive: props.plan === 'bundle' }" :highlight="props.highlight" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import VideoPlayer from '@itbpbg/lms-components/src/components/VideoPlayer';
import CourseCard from '@itbpbg/lms-components/src/Courses/Card';
import CloseIcon from '@itbpbg/lms-components/src/assets/svg/close-icon.svg?vue-component';
import { countDownToDate } from '@itbpbg/lms-components/src/utils/helper';
import PlusSign from '../../assets/svg/icon-plus-sign.svg?vue-component';
import HeroSection from './HeroSection';
import Curriculum from './Curriculum';
import PriceCard from './PriceCard';
import Cards from './Cards';
import ProgramCardsSale from './ProgramCardsSale';
import FAQ from './FAQ';
import Reviews from '../Landing/Reviews';
import LimitedOffer from '../Landing/LimitedOffer';
import IconCheck from '../../assets/svg/icon-check.svg?vue-component';
import LogoTesla from '../../assets/svg/companies/logo-tesla.svg?vue-component';
import LogoWalmart from '../../assets/svg/companies/logo-walmart.svg?vue-component';
import LogoCitibank from '../../assets/svg/companies/logo-citibank.svg?vue-component';
import LogoBooking from '../../assets/svg/companies/logo-booking.svg?vue-component';
import LogoPaypal from '../../assets/svg/companies/logo-paypal.svg?vue-component';

export default {
  components: {
    VideoPlayer,
    CourseCard,
    PlusSign,
    HeroSection,
    Curriculum,
    PriceCard,
    Cards,
    ProgramCardsSale,
    FAQ,
    Reviews,
    LimitedOffer,
    IconCheck,
    LogoTesla,
    LogoWalmart,
    LogoCitibank,
    LogoBooking,
    LogoPaypal,
    CloseIcon,
  },
  mixins: [global],
  props: {
    seo: { type: Object, default: () => {}, required: true },
    courseInfo: { type: Object, default: () => { }, required: true },
    stats: { type: Object, default: () => { }, required: false },
  },
  data() {
    return {
      page: {},
      videoJSLoaded: false,
      courseData: {},
      limitedOfferFeatures: [
        {
          name: '',
        },
        {
          name: 'Content updates',
        },
        {
          name: 'Exam',
        },
        {
          name: this.courseInfo.slug !== 'product-management-for-ai-data-science' ? 'Learning resources' : 'Downloadable resources',
        },
        {
          name: 'Certificate of achievement',
        },
        {
          name: 'Resume builder',
        },
        {
          name: 'Self-paced learning',
        },
        {
          name: 'Lifetime access',
        },
      ],
      limitedOfferOptions: {
        title: this.courseInfo.limitedOfferOptionsTitle || 'Take Your Data Skills to the Next Level',
        regularPrice: '',
        discountedPrice: '',
        promoPlanButtonText: '',
        course: true,
        badges: this.courseInfo.badges,
      },
      bottomOfferInfo: {
        title: 'Build Your Desired Skillset',
        description: 'Whether searching for an all-encompassing data science program or focused comprehensive machine learning training, our top-rated platform is the best place to learn.',
        plans: [
          {
            title: 'Premium Annual Plan',
            highlight: 'yellow',
            plan: 'annual',
            regularPrice: '432.00',
            price: this.courseInfo.annualPlan.discountedPrice || this.courseInfo.annualPlan.price,
            billed: 'Billed annually',
            discountPercent: Math.round((1 - ((this.courseInfo.annualPlan.discountedPrice || this.courseInfo.annualPlan.price) / 432)) * 100),
            href: '/checkout/?plan=annual',
            specifications: [
              { name: `${this.stats.rounded.videoDuration} hours of video`, checked: true },
              { name: `${this.stats.rounded.exercises} exercises`, checked: true },
              { name: 'Downloadables', checked: true },
              { name: 'Self-paced lessons', checked: true },
              { name: 'Resume builder', checked: true },
              { name: 'Gamified features', checked: true },
              { name: 'Leaderboard', checked: true },
              { name: 'Career tracks', checked: true },
              { name: 'Exams', checked: true },
              { name: 'Certificates', checked: true },
              { name: 'New content', checked: true },
              { name: 'Portfolio advice', checked: true },
              { name: 'Resume feedback', checked: true },
              { name: 'Personalized support', checked: true },
            ],
          },
          {
            title: 'Machine Learning A-Z Bundle',
            highlight: 'blue',
            plan: 'bundle',
            regularPrice: 0,
            price: 249,
            billed: 'Billed once',
            discountPercent: 0,
            href: `/checkout/?plan=${this.courseInfo.plan}&ptype=individual-course`,
            specifications: [
              { name: `${this.courseInfo.lessons} video lessons`, checked: true },
              { name: `${this.courseInfo.courses.length} course exams`, checked: true },
              { name: `${this.courseInfo.courses.length} certificates`, checked: true },
              { name: 'Lifetime access', checked: true },
              { name: 'Content updates', checked: true },
              { name: 'Focused curriculum', checked: true },
              { name: 'Self-paced learning', checked: true },
              { name: 'Links to additional resources', checked: true },
              { name: 'Coding walkthroughs', checked: true },
              { name: 'Business use cases', checked: true },
              { name: 'Renowned instructors', checked: true },
              { name: 'User dashboard', checked: true },
              { name: 'Resume builder', checked: true },
              { name: 'Build-in flashcards', checked: true },
            ],
          },
        ],
      },
      banner: {
        startDate: this.courseInfo.promotion.startDate,
        endDate: this.courseInfo.promotion.endDate,
        active: false,
      },
      countdownEndDate: null,
      countdown: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      },
    };
  },
  head() {
    const metaTitle = `${this.page.metaTitle}`;

    const instructor = this.courseInfo.instructorsArr.map((i) => ({
      '@type': 'Person',
      name: i.name,
    }));

    let hasCourseInstance = [];

    if (this.courseInfo.courses && this.courseInfo.courses.length) {
      hasCourseInstance = this.courseInfo.courses.map((c) => ({
        '@type': 'CourseInstance',
        instructor,
        courseMode: 'online',
        accessMode: 'visual',
        courseWorkload: `PT${c.totalHours}H`,
        educationalCredentialAwarded: `Certificate of completion - ${c.name}`,
        url: `${this.$config.appUrl}/courses/${c.slug}/`,
      }));
    } else {
      hasCourseInstance.push({
        '@type': 'CourseInstance',
        instructor,
        courseMode: 'online',
        accessMode: 'visual',
        courseWorkload: `PT${this.courseInfo.totalHours}H`,
        educationalCredentialAwarded: `Certificate of completion - ${this.courseInfo.name}`,
        url: `${this.$config.appUrl}/courses/${this.courseInfo.slug}/`,
      });
    }

    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
      {
        key: 'Course',
        values: {
          name: metaTitle,
          description: this.page.metaDescription,
          hasCourseInstance,
          offers: [{
            '@type': 'Offer',
            category: 'Paid',
            priceCurrency: 'USD',
            price: 36.00,
          }],
        },
      },
    ]);

    const link = [];

    if (this.canonicalURL) {
      link.push({ rel: 'canonical', href: this.canonicalURL() });
    }

    return {
      titleTemplate: `${metaTitle}%s`,
      link,
      meta: this.metaProps({ ...this.page, metaImage: `og-${this.course.slug}.png` }),
      script: [
        { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js', async: true },
        linkedData,
      ],
    };
  },
  computed: {
    learn() {
      let json = {};
      try {
        json = JSON.parse(`${this.course.learn}`);
      } catch (e) {
        json = {};
      }
      return json;
    },
    bannerNotExpired() {
      return new Date() <= new Date(this.banner.endDate);
    },
    bannerAllowedPath() {
      return ['/learn-machine-learning-process-a-z/', '/machine-learning-course-bundle/'].includes(this.$route.path);
    },
    bannerActive() {
      return this.banner && this.banner.active && this.bannerNotExpired && this.bannerAllowedPath && new Date(this.banner.startDate) <= new Date();
    },
    course() {
      if (this.bannerActive && this.courseInfo.bundle) {
        return { ...this.courseInfo, ...this.courseData };
      }

      return this.courseInfo;
    },
    bundleDiscounts() {
      return {
        bundle: {
          href: `/checkout/?plan=${this.course.plan}&ptype=individual-course${this.course.coupon ? `&coupon=${this.course.coupon}` : ''}`,
          discountPercent: this.course.discountPercent,
          regularPrice: this.course.regularPrice,
          price: this.course.discountedPrice,
        },
        annual: {
          href: '/checkout/?plan=annual&coupon=KENJEFF60A',
          regularPrice: '432.00',
          price: '174.00',
          discountPercent: 60,
        },
      };
    },
  },
  created() {
    this.page = this.seo;
    this.page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
    this.page.created = this.page.created || new Date();
    this.page.updated = this.page.updated || new Date();
    this.countdownEndDate = this.saleEndDate();
    this.bannerCountdown();
  },
  async mounted() {
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJSLoaded = true;
    this.changeLimitedOfferProps();
    if (this.countdown) {
      const x = setInterval(async () => {
        this.bannerCountdown();
        if (!this.countdown || !this.banner) {
          this.countdown = {
            days: '00',
            hours: '00',
            minutes: '00',
            seconds: '00',
          };
          clearInterval(x);
          // this.$emit('offer-expired');
          this.banner = false;
          if (this.bannerAllowedPath) {
            window.$nuxt.refresh();
          }
        }
      }, 1000);
    }
  },
  methods: {
    async fetchCourse(promo) {
      this.courseData = await this.$axios.$get(`/courses/landing${this.$route.path}${promo ? '' : '?stoppromo=1'}`).catch(() => ({}));
      this.changeLimitedOfferProps();
    },
    subscribe() {
      const query = { plan: this.course.plan, ptype: 'individual-course' };
      const coupon = this.$route.query.coupon || this.course.coupon;
      if (coupon) {
        query.coupon = coupon;
      }

      return this.$router.push({ path: '/checkout/', query });
    },
    saleEndDate() {
      const endDateUtc = this.banner.endDate;
      const localCountDownDate = new Date(endDateUtc);
      return localCountDownDate;
    },
    changeLimitedOfferProps() {
      this.limitedOfferFeatures[0].name = `${this.course.lessons} Video Lessons`;
      this.limitedOfferOptions.regularPrice = `$${this.course.regularPrice}`;
      this.limitedOfferOptions.discountedPrice = `$${this.course.discountedPrice}`;
      this.limitedOfferOptions.promoPlanButtonText = this.course.discountPercent ? `Save ${this.course.discountPercent}% Now` : 'Get Now';
    },
    async bannerCountdown() {
      const countDownDate = new Date(this.countdownEndDate);
      const interval = countDownToDate(new Date(), countDownDate);
      this.countdown = interval;
      if (this.banner && new Date(this.banner.startDate) <= new Date() && this.countdown && !this.banner.active) {
        this.banner.active = true;
        window.$nuxt.refresh();
      }
    },
  },
};
</script>

<style lang="scss">
.course-landing {
  &.banner:before {
    content: '';
    height: 171px;
    background-color: var(--main-dark);
    width: 100%;
    display: block;
  }

  .site-section {
    padding: var(--spacing-100) 0;

    .container {
      position: relative;
      z-index: 1;
    }

    &.gray {
      background-color: var(--main-light-shade-1);

      .faq-holder .questions .col-6 .qa-holder:last-of-type {
        margin-bottom: 0;
      }
    }

    &.dark {
      position: relative;
    }
  }

  .btn-primary {
    width: 100%;
  }

  .start {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    h2 {
      text-align: center;
      margin-bottom: var(--spacing-24);
    }

    .body-1 {
      margin: 0 0 var(--spacing-60);
      text-align: center;
    }

    .bundle {
      border-radius: 20px;
      padding: 100px 0;
      background-color: var(--main-dark);
      display: flex;
      justify-content: center;
      align-items: center;

      .plus-sign {
        margin: 0 10px;
      }
    }

    .stats {
      display: flex;
      justify-content: space-between;
      text-align: center;
      margin-top: var(--spacing-100);

      .stat {
        position: relative;
        flex: 1;

        div {
          font-size: 57px;
          font-weight: 600;
          margin-bottom: var(--spacing-8);
        }

        span {
          color: var(--main-dark-shade-3);
        }

        + .stat:before {
          content: '';
          position: absolute;
          width: 1px;
          top: 4px;
          bottom: 4px;
          left: 0;
          background-color: var(--main-dark-shade-4);
        }
      }
    }
  }

  .video {
    margin-top: var(--spacing-100);
  }

  .learn {
    padding-top: var(--spacing-80);
    padding-bottom: var(--spacing-80);
    text-align: center;
    position: relative;

    h2 {
      margin: 0 0 11px;
    }

    p {
      margin: 0 0 var(--spacing-36);
    }

    ul {
      display: flex;
      list-style: none;
      margin: 0;
      padding: 0;
      flex-flow: row;
      flex-wrap: wrap;

      li {
        text-align: left;
        display: flex;
        align-items: flex-start;
        padding-bottom: var(--spacing-26);
        word-break: break-word;

        &:not(:nth-of-type(5)):not(:nth-of-type(6)) {
          margin-bottom: var(--spacing-36);
        }

        svg {
          width: 20px;
          height: 20px;
          margin-right: var(--spacing-12);
          flex: 0 0 auto;

          path[fill] {
            fill: var(--accent);
          }
        }

        &:nth-of-type(even) {
          padding-left: 15px;
        }

        &:nth-of-type(odd) {
          padding-right: 15px;
        }
      }
    }

    hr {
      border: none;
      height: 1px;
      background-color: rgba(255, 255, 255, 0.2);
      margin: var(--spacing-36) 0;
    }

    span {
      color: var(--main-light-shade-4);
    }

    .logos {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin-top: var(--spacing-12);
      margin-left: -16px;
      margin-right: -16px;

      .logo {
        margin: var(--spacing-16);
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 128px;

          path[fill="#fff"] {
            fill: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  .meet-author {
    padding-top: var(--spacing-80);
    padding-bottom: var(--spacing-100);
    position: relative;

    img {
      width: 100%;
      height: 100%;
      border-radius: 10px;
    }

    .info {
      h2 {
        margin-bottom: var(--spacing-36);
      }

      .body-1 {
        &:first-of-type {
          margin-top: 0;
        }

        &:last-of-type {
          margin-bottom: 0;
        }
      }

      .stats {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: var(--spacing-36);

        .stat {
          display: flex;
          align-items: center;

          div {
            font-size: 57px;
            font-weight: 500;
            margin-right: var(--spacing-12);
          }

          span {
            font-size: 18px;
            color: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  .buy-course {
    padding-top: var(--spacing-100);
    text-align: center;

    h2 {
      margin-bottom: var(--spacing-48);
    }

    > div > div {
      display: flex;
    }

    .btn-info {
      margin: 0 auto;
      max-width: 350px;

      p {
        color: var(--main-light-shade-4);
      }
    }
  }

  .reviews {
    padding-bottom: var(--spacing-100);

    .reviews-trustpilot {
      .reviews-heading {
        > div {
          .heading.title {
            font-size: 40px;
            font-weight: 500;
          }
        }
      }
    }
  }

  .limited-offer-holder {
    padding: var(--spacing-100) 0;
    background-color: var(--main-dark);
    color: var(--main-light);
    position: relative;

    .container {
      z-index: 1;
      position: relative;
    }

    .heading {
      margin-bottom: 0;
    }

    hr {
      background-color: rgba(255, 255, 255, 0.24);
    }

    .plan-features {
      margin-bottom: var(--spacing-32);

      .feature {
        margin-bottom: var(--spacing-16);
      }
    }

    button {
      max-width: 60%;
    }

    .vat-note {
      font-size: 16px;
      color: var(--main-light-shade-4);
    }
  }

  .bottom-offer {
    padding: var(--spacing-100) 0;
    background-color: var(--main-dark);

    .head {
      color: var(--main-light);
      text-align: center;

      .body-1 {
        margin: var(--spacing-12) 0 0;
      }
    }

    .content {
      margin-top: var(--spacing-60);
    }
  }

  @include bp($bp-laptop) {
    .start .stats .stat div {
      font-size: 54px;
      line-height: 100%;
    }

    .meet-author .info .stats .stat div {
      font-size: 44px;
    }

    .reviews .reviews-trustpilot {
      .reviews-heading > div .heading.title {
        font-size: 32px;
      }

      .reviews-body .card-trustpilot-review .heading {
        font-size: 14px;
      }
    }

    .limited-offer-holder button {
      max-width: 63%;
    }

    .buy-course .button {
      max-width: 310px;
    }
  }

  @include bp($bp-mobile) {
    h2 {
      font-size: 32px;
    }

    .site-section {
      &:not(.gray):not(.dark) {
        padding-top: var(--spacing-100);
        padding-bottom: var(--spacing-100);
      }

      &.gray {
        padding: var(--spacing-100) 0;

        .curriculum {
          .container {
            .heading {
              padding-bottom: var(--spacing-4);
            }
          }
        }

        .reviews {
          .reviews-trustpilot {
            .reviews-heading {
              > div {
                .heading.title {
                  font-size: 28px;
                }
              }
            }
          }
        }
      }

      &.dark {
        padding-bottom: var(--spacing-80);

        &:before,
        &:after {
          display: none;
        }
      }
    }

    .start {
      .stats {
        flex-wrap: wrap;

        .stat + .stat:before {
          content: none;
        }
      }
    }

    .video {
      padding-top: var(--spacing-60);

      .player.video-player.video-js {
        border-radius: 10px;
      }
    }

    .learn {
      padding-top: var(--spacing-40);
      padding-bottom: var(--spacing-40);

      &:before {
        display: none;
      }

      h2,
      .body-1 {
        text-align: left;
      }

      ul {
        grid-template-columns: 1fr;
        padding: 0;

        li:nth-of-type(5) {
          margin-bottom: var(--spacing-36);
        }

        li:nth-of-type(even) {
          padding-left: 0;
        }
      }
    }

    .meet-author {
      flex-direction: column;
      padding-bottom: var(--spacing-40);

      &:before {
        display: none;
      }

      img {
        display: block;
        margin: 0 auto var(--spacing-36);
        width: 100%;
        height: 100%;
        max-width: 500px;
      }

      .info {
        h2 {
          margin-bottom: var(--spacing-16);
        }

        .stats {
          flex-direction: column;
          align-items: flex-start;

          .stat:first-of-type {
            margin-bottom: var(--spacing-36);
          }
        }
      }
    }

    .buy-course {
      padding-top: var(--spacing-80);
    }

    .reviews-trustpilot {
      .reviews-heading {
        > div {
          .heading.title {
            font-size: 28px;
          }
        }
      }
    }

    .limited-offer {
      &:before,
      &:after {
        display: none;
      }

      .price {
        .discounted-price {
          font-size: 40px;
        }

        .regular-price {
          font-size: 24px;
        }
      }

      .plan-features {
        grid-column-gap: var(--spacing-36);
        grid-row-gap: var(--spacing-16);

        .feature {
          font-size: 14px;
        }
      }
    }

    .bottom-offer .content {
      .price-card {
        margin: 0 auto;
        max-width: 400px;
      }

      .col {
        position: relative;
        z-index: 1;
      }

      .col + .col {
        order: -1;
        z-index: 0;
        margin-bottom: 36px;
      }
    }
  }

  @include bp($bp-laptop-tablet) {
    .start .bundle {
      flex-wrap: wrap;

      .course-card:first-child + .plus-sign {
        display: none;
      }

      .plus-sign {
        width: 100%;
        display: flex;
        justify-content: center;
        margin-bottom: var(--spacing-30);
      }

      .line-break {
        content: '';
        display: block;
        width: 100%;
      }
    }
  }

  @include bp($bp-tablet) {
    .course-card {
      max-width: 340px;
    }
  }

  @include bp($bp-phone) {
    h2 {
      font-size: 28px;
    }

    .site-section {
      &.dark {
        padding-top: var(--spacing-80);

        .cards-heading {
          margin-bottom: var(--spacing-36);
        }

        .cards {
          .card {
            margin: 0 0 var(--spacing-16);
          }
        }
      }

      &.gray {
        padding-bottom: var(--spacing-80);

        .reviews {
          padding-bottom: var(--spacing-30);

          .reviews-trustpilot .reviews-heading > div {
            margin-bottom: var(--spacing-36);
          }
        }

        .faq-holder {
          padding-top: var(--spacing-80);
        }
      }
    }

    .module-holder {
      .module .module-body .body-2 {
        margin-bottom: var(--spacing-16);
      }
    }

    .learn {
      padding-top: var(--spacing-80);
      padding-bottom: var(--spacing-80);

      ul li {
        &:nth-of-type(odd) {
          padding-left: 0;
          padding-right: 0;
        }

        &:nth-of-type(even) {
          padding-left: 0;
          padding-right: 0;
        }
      }
    }

    .limited-offer {
      .plan-features {
        .body-2 {
          margin-bottom: var(--spacing-12);
        }
      }

      .btn-lg {
        max-width: 100%;
        margin-top: var(--spacing-20);
      }
    }

    .buy-course {
      .btn-info p {
        margin-bottom: 0;
      }

      h2 {
        margin-bottom: var(--spacing-40);
      }
    }

    .bundle-row {
      margin-left: -24px;
      margin-right: -24px;
    }

    .start {
      .bundle {
        border-radius: 0;
        flex-direction: column;

        .plus-sign {
          margin-bottom: 30px;
        }
      }

      .stats .stat {
        flex: 0 0 50%;
        margin: 10px 0;
      }
    }
  }

  .banner-holder {
    margin: 0;
    top: 80px;
    text-decoration: none;
    text-align: center;
    width: 100%;
    outline: 0;
    appearance: none;
    border: 0;
    position: fixed;
    background: linear-gradient(270deg, #8CDAE3 -7.92%, #7B61FF 91.79%);
    box-shadow: 0 16px 30px rgba(0, 12, 31, 0.15);
    padding: 12px 0;
    z-index: 12;

    .close-banner {
      position: absolute;
      top: 14px;
      right: 28px;
      mix-blend-mode: overlay;

      svg {
        cursor: pointer;
        height: 16px;

        path[fill] {
          fill: var(--light-purple);
        }
      }
    }

    .banner {
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-sizing: border-box;
      border-radius: 10px;
      cursor: pointer;

      .header-wrap {
        width: auto;
      }

      .banner-header {
        margin: 0;
        text-align: left;
        font-weight: 500;
        font-size: 28px;
        line-height: 110%;
        color: var(--main-light);
      }

      .banner-info {
        text-align: left;
        margin: 4px 0 0;
        font-weight: 500;
        font-size: 18px;
        line-height: 110%;
        color: var(--main-light);
      }

      .btn-primary-purple-dark {
        height: 50px;
        color: var(--main-light);
        border: 0;
        outline: 0;
        width: 184px;
        padding: 10px;
      }

      .counter {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-right: 15px;

        .square {
          width: 80px;
          height: 68px;
          box-sizing: border-box;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-direction: column;
          margin: 0 var(--spacing-4);
          padding: 10px 12px;
          border: 1px solid #B8BFFF;
          border-radius: 10px;

          span {
            width: 50px;
            font-size: 28px;
            line-height: 32px;
            color: var(--main-light);

            &:last-of-type {
              font-weight: 400;
              font-size: 12px;
              line-height: 20px;
            }
          }
        }

        .square:first-child {
          margin-left: 0;
        }

        .square:last-child {
          margin-right: 0;
        }

        .colon {
          font-size: 32px;
          line-height: 150%;
          color: var(--main-light-shade-2);
        }
      }
    }
  }

  @include bp($bp-mobile) {
    .banner-holder {
      top: 68px;

      .banner {
        max-width: 344px;
        flex-direction: column;

        .banner-header,
        .banner-info {
          text-align: center;
        }

        .header-wrap {
          margin-bottom: var(--spacing-16);
        }

        .counter {
          margin: 0;

          .square {
            width: 66px;
          }
        }

        .btn-primary-purple-dark {
          margin-top: var(--spacing-16);
          width: 100%;
        }
      }
    }
  }

  &.mastering-key-performance-indicators {
    .limited-offer .plan-features .feature {
      display: flex;
      align-items: flex-start;

      svg {
        flex: 0 0 auto;

        path[stroke] {
          stroke: var(--main-light);
        }
      }
    }
  }
}
</style>
